/**
 * Management API client.
 *
 * The session lives in an HttpOnly cookie; the CSRF token lives in a readable
 * cookie and is echoed back in a header on every state-changing request. The
 * browser never handles a Hub API key or an upstream credential.
 */

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly requestId: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function csrfToken(): string {
  const match = document.cookie.split('; ').find((entry) => entry.startsWith('ah_csrf='));
  return match ? decodeURIComponent(match.slice('ah_csrf='.length)) : '';
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (!['GET', 'HEAD'].includes(method)) headers['x-csrf-token'] = csrfToken();

  const response = await fetch(path, {
    method,
    headers,
    credentials: 'same-origin',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  const text = await response.text();
  const payload = text.length > 0 ? (JSON.parse(text) as Record<string, unknown>) : {};

  if (!response.ok) {
    const error = (payload.error ?? {}) as {
      code?: string;
      message?: string;
      requestId?: string;
      details?: unknown;
    };
    throw new ApiError(
      error.code ?? 'INTERNAL_ERROR',
      error.message ?? `Żądanie nie powiodło się (${response.status}).`,
      response.status,
      error.requestId ?? '',
      error.details,
    );
  }

  return (payload.data ?? payload) as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};

/** Formats an API error for display, keeping the request id visible. */
export function describeError(error: unknown): { title: string; body: string } {
  if (error instanceof ApiError) {
    const issues = (
      error.details as { issues?: Array<{ path: string; message: string }> } | undefined
    )?.issues;
    const detail = issues?.map((issue) => `${issue.path}: ${issue.message}`).join('; ');
    return {
      title: error.message,
      body: [error.code, detail, error.requestId && `requestId=${error.requestId}`]
        .filter(Boolean)
        .join(' · '),
    };
  }
  return { title: 'Wystąpił błąd', body: error instanceof Error ? error.message : String(error) };
}
